<!-- LoadingScreen.svelte - Красивый неоновый экран загрузки -->
<script lang="ts">
  import { onMount } from 'svelte';
  import { animate } from 'motion';
  import { eventBus } from '@/engine/events/EventBus';
  import { logger } from '@/engine/logging';

  // Import assets properly for Vite build
  import backgroundImage from '@/assets/loader/background.png';
  import logoImage from '@/assets/loader/logo.png';

  // Props
  export let progress: number = 0;
  export let status: string = 'Initializing...';
  export let isVisible: boolean = true;

  // Logo positioning settings
  export let logoTop: string = '49%';
  export let logoTransform: string = 'translateY(-49%)';

  // Callback for when loading is ready to hide
  export let onLoadingReady: (() => void) | undefined = undefined;

  let statusElement: HTMLDivElement;
  let logoElement: HTMLDivElement;
  let progressFillElement: HTMLDivElement;

  // Loading steps with descriptions
  const loadingSteps = [
    { id: 'core', label: 'Initializing Core Systems', weight: 10 },
    { id: 'canvas', label: 'Setting up Canvas', weight: 5 },
    { id: 'game', label: 'Loading Game Engine', weight: 20 },
    { id: 'assets', label: 'Loading Assets', weight: 30 },
    { id: 'textures', label: 'Loading Textures', weight: 20 },
    { id: 'ui', label: 'Preparing Interface', weight: 10 },
    { id: 'complete', label: 'Ready to Launch!', weight: 5 },
  ];

  let currentStep = 0;

  onMount(() => {
    // Animate progress bar with CSS
    if (progressFillElement) {
      const scaleValue = (progress / 100).toString();
      progressFillElement.style.setProperty('--scale-x', scaleValue);
    }

    // Animate status text
    if (statusElement) {
      animate(
        statusElement,
        {
          opacity: 1,
          y: 0,
        },
        {
          duration: 0.5,
          delay: 0.2,
        }
      );
    }

    // Логотип сразу видимый и стабильный
    if (logoElement) {
      logoElement.style.opacity = '1';
    }

    // Listen for loading events
    eventBus.on('loading-progress', handleProgressUpdate);
    eventBus.on('loading-step', handleStepUpdate);

    return () => {
      eventBus.off('loading-progress', handleProgressUpdate);
      eventBus.off('loading-step', handleStepUpdate);
    };
  });

  function handleProgressUpdate(data: { progress: number; status: string }) {
    progress = data.progress;
    status = data.status;

    // Animate progress bar with CSS
    if (progressFillElement) {
      const scaleValue = (progress / 100).toString();
      progressFillElement.style.setProperty('--scale-x', scaleValue);
    }

    // Animate status text change
    if (statusElement) {
      animate(
        statusElement,
        {
          opacity: 0.7,
          y: -10,
        },
        {
          duration: 0.1,
        }
      ).then(() => {
        animate(
          statusElement,
          {
            opacity: 1,
            y: 0,
          },
          {
            duration: 0.3,
          }
        );
      });
    }

    // Уведомляем родительский компонент о завершении загрузки
    if (progress >= 100 && onLoadingReady) {
      setTimeout(() => {
        onLoadingReady();
      }, 500); // Небольшая задержка для плавности
    }
  }

  function handleStepUpdate(data: { step: string }) {
    const stepIndex = loadingSteps.findIndex(step => step.id === data.step);
    if (stepIndex !== -1) {
      currentStep = stepIndex;
    }
  }

  function hideLoadingScreen() {
    isVisible = false;
    logger.info('🎉 Loading screen hidden', { source: 'LoadingScreen' });
  }

  // Expose hide function for parent
  export { hideLoadingScreen };
</script>

{#if isVisible}
  <div class="loading-screen">
    <!-- Background image -->
    <div class="loading-background">
      <img src={backgroundImage} alt="Loading background" class="background-image" />
    </div>

    <!-- Main content -->
    <div class="loading-content">
      <!-- Logo - стабильный в центре -->
      <div
        bind:this={logoElement}
        class="logo-container"
        style="top: {logoTop}; transform: translateX(-50%) {logoTransform};"
      >
        <img src={logoImage} alt="Neon Miner Logo" class="logo-image" />
      </div>

      <!-- Progress section - внизу экрана -->
      <div class="progress-section">
        <!-- Progress bar -->
        <div class="progress-container">
          <div class="progress-track">
            <div bind:this={progressFillElement} class="progress-bar"></div>
            <div class="progress-text">{Math.round(progress)}%</div>
          </div>
        </div>

        <!-- Status text -->
        <div bind:this={statusElement} class="status-text">
          {status}
        </div>

        <!-- Loading steps indicator -->
        <div class="steps-indicator">
          {#each loadingSteps as _, index}
            <div
              class="step-dot"
              class:active={index <= currentStep}
              class:current={index === currentStep}
            ></div>
          {/each}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .loading-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .loading-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .background-image {
    height: 100vh;
    width: auto;
    object-fit: cover;
    object-position: center;
    opacity: 1;
  }

  .loading-content {
    position: relative;
    z-index: 2;
    height: 100vh;
    width: 100%;
    text-align: center;
  }

  .logo-container {
    position: absolute;
    left: 50%;
    z-index: 3;
  }

  .logo-image {
    height: 200px;
    width: auto;
    max-width: 400px;
    object-fit: contain;
    filter: drop-shadow(0 0 30px #a353de);
    /* Убираем анимацию - логотип стабильный */
  }

  .progress-section {
    position: absolute;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    z-index: 3;
  }

  .progress-container {
    width: 100%;
    padding: 0 2rem;
  }

  .progress-track {
    height: 24px;
    background: rgba(0, 0, 0, 0.9);
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid #00ffff;
    box-shadow: inset 0 0 10px rgba(0, 255, 255, 0.3);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, #00ffff 0%, #ff00ff 50%, #ff0080 100%);
    box-shadow: 0 0 15px #00ffff;
    border-radius: 10px;
    transform: scaleX(var(--scale-x, 0));
    transform-origin: left;
    transition: transform 0.3s ease-out;
  }

  .progress-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Orbitron', monospace;
    font-size: 1rem;
    color: #ffffff;
    text-shadow:
      0 0 10px #000000,
      0 0 5px #00ffff;
    font-weight: bold;
    z-index: 2;
  }

  .status-text {
    font-family: 'Rajdhani', sans-serif;
    font-size: 1.2rem;
    color: #ffffff;
    font-weight: 600;
    text-shadow: 0 0 10px #00ffff;
  }

  .steps-indicator {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .step-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--bg-tertiary);
    border: 1px solid var(--neon-cyan);
    transition: all 0.3s ease;
  }

  .step-dot.active {
    background: var(--neon-cyan);
    box-shadow: 0 0 10px var(--neon-cyan);
  }

  .step-dot.current {
    background: var(--neon-purple);
    box-shadow: 0 0 15px var(--neon-purple);
    transform: scale(1.2);
  }

  /* Animations */
  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
</style>
